import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Shield } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LockedContent } from "@/components/layout/locked-content";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

import { cn, message } from "@/lib/utils";
import { getScoreBarColor, getScoreStrokeColor } from "@/lib/chart";
import type { CreativesScorecardData, CreativesScorecardSubBrandOverviewItem } from "@/schema/brand-os/brand-governance";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";

interface SubBrandRowItem {
  id: string;
  name: string;
  score: number;
  assets: number;
  issues: number;
}

function brandConfig(item: CreativesScorecardSubBrandOverviewItem, index: number): SubBrandRowItem {
  return {
    id: `${item.sub_brand}-${index}`,
    name: item.sub_brand,
    score: item.score ?? 0,
    assets: item.total_creatives ?? 0,
    issues: item.num_issues ?? 0,
  };
}

function BrandHealthComponent({ status, data }: { status: QueryStatus; data?: CreativesScorecardData | null }) {
  switch (status) {
    case "pending":
      return <SubBrandHealthSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <SubBrandHealthContent data={data} /> : <LockedContent message={message} />;
  }
}

export function SubBrandHealth({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
          <Shield className="size-4 text-purple-600" />
        </div>
        <CardTitle>Sub-Brand Health</CardTitle>
      </CardHeader>
      <CardContent className="max-h-72 overflow-y-auto scrollbar-hidden">
        <BrandHealthComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function SubBrandHealthContent({ data }: { data: CreativesScorecardData }) {
  const subBrands = (data.sub_brand_overview ?? []).map(brandConfig);

  if (!subBrands.length) {
    return <LockedContent message={message} />;
  }

  return (
    <div className="flex flex-col divide-y divide-border">
      {subBrands.map((subBrand) => (
        <SubBrandRow key={subBrand.id} subBrand={subBrand} />
      ))}
    </div>
  );
}

function SubBrandRow({ subBrand }: { subBrand: SubBrandRowItem }) {
  const barClass = getScoreBarColor(subBrand.score);
  const { text: scoreTextClass } = getScoreStrokeColor(subBrand.score);

  return (
    <div className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2">
        <div className={cn("size-2 shrink-0 rounded-full", barClass)} aria-hidden />
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">{subBrand.name}</span>
        <div className="flex shrink-0 items-center gap-3">
          <span className={cn("text-sm font-medium", scoreTextClass)}>{subBrand.score}</span>
          <span className="text-sm text-muted-foreground/60">{subBrand.assets} assets</span>
          <span className="rounded-md bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive">{subBrand.issues} issues</span>
        </div>
      </div>
      <Progress value={subBrand.score} className="h-2 bg-muted" indicatorClassName={barClass} />
    </div>
  );
}

function SubBrandHealthSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-border">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
          <div className="flex items-center gap-2">
            <Skeleton className="size-2 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1 max-w-[120px] rounded-md" />
            <div className="flex shrink-0 items-center gap-3">
              <Skeleton className="h-4 w-8 rounded-md" />
              <Skeleton className="h-4 w-14 rounded-md" />
              <Skeleton className="h-6 w-14 rounded-md" />
            </div>
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
